import { cn } from '@listening/utils/react-helpers';
import CheckSmallGreenIcon from '@listening/assets/astro/icons/check-small-green.svg?react';
import listeningLogoPng from '@listening/assets/astro/images/listening-logo.png';

import { Slider } from '@listening/frontend/components/radix/slider';
import { Zenitho } from 'uvcanvas';
import { ZenithoFallback } from '@/components/shared-ui/zenitho-fallback';

function StepIndicator({
  className,
  position,
  label,
  currentStep,
}: {
  className?: string;
  position: number;
  label: string;
  currentStep: number;
}) {
  const idx = position - 1;
  const state =
    currentStep === idx
      ? 'active'
      : currentStep > idx
        ? 'complete'
        : 'inactive';
  return (
    <div
      className={cn(
        'flex min-w-0 items-center justify-center gap-1 rounded-full py-2 transition-all sm:gap-2 md:gap-3',
        state === 'active' && 'px-2 md:px-4',
        state === 'complete' && 'gap-2',
        className,
      )}
    >
      {state !== 'inactive' && <CheckSmallGreenIcon className='shrink-0' />}

      <span className={cn('text-sm font-medium text-neutral-700 md:text-lg')}>
        {position}
      </span>
      <span
        className={cn(
          'text-[0.6rem] leading-none font-medium whitespace-nowrap text-neutral-700 sm:leading-[1.2] md:text-sm',
        )}
      >
        {label}
      </span>
    </div>
  );
}

function ProgressIndicator({ value }: { value: number }) {
  return (
    <Slider
      className='h-[6px] w-full'
      defaultValue={[0]}
      disabled
      max={100}
      min={0}
      rangeClassName='rounded-full transition-all duration-300'
      rangeComponent={<Zenitho fallback={<ZenithoFallback />} />}
      thumbClassName={null} // TODO: Make it so I can swap colors. https://github.com/latentcat/uvcanvas/issues/4#issuecomment-1977769672
      value={[value]}
    />
  );
}

export function AnimatedHeading({
  pages,
  currentStep,
}: {
  pages: Array<{
    label: string;
  }>;
  currentStep: number;
}) {
  const progressValue = Math.max(
    50,
    ((currentStep + 1) / pages.length) * 50 + 50,
  );

  return (
    <div className='mb-4 flex w-full flex-col gap-6 sm:mb-0'>
      <div className='flex flex-col items-center gap-3 overflow-hidden pt-4 md:pt-6 lg:flex-row lg:items-center lg:justify-between lg:gap-18 lg:px-18'>
        <img alt='Listening logo' className='w-40' src={listeningLogoPng} />
        <div className='flex w-full items-center justify-center gap-2 px-4 sm:gap-4 md:gap-6 lg:w-auto lg:justify-end lg:px-0'>
          {pages.map((page, idx) => (
            <StepIndicator
              currentStep={currentStep}
              key={page.label}
              label={page.label}
              position={idx + 1}
            />
          ))}
        </div>
      </div>
      <ProgressIndicator value={progressValue} />
    </div>
  );
}
